import { computed } from 'vue'

import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

export type QualificationDomain = 'transfer' | 'documental'

/**
 * Permissions de escrita por dominio de qualificacao (Pos-Plantao).
 * Repasse -> Analista Financeiro; Documental -> Analista de Documentacao.
 */
const DOMAIN_PERMISSIONS: Record<
  QualificationDomain,
  {
    creating: AuthorizationPermissions
    editing: AuthorizationPermissions
    toggling: AuthorizationPermissions
    doctorsManaging: AuthorizationPermissions
  }
> = {
  transfer: {
    creating: AuthorizationPermissions.QualificationTransferPolicyCreating,
    editing: AuthorizationPermissions.QualificationTransferPolicyEditing,
    toggling: AuthorizationPermissions.QualificationTransferPolicyToggling,
    doctorsManaging: AuthorizationPermissions.QualificationTransferDoctorsManaging
  },
  documental: {
    creating: AuthorizationPermissions.QualificationDocumentalPolicyCreating,
    editing: AuthorizationPermissions.QualificationDocumentalPolicyEditing,
    toggling: AuthorizationPermissions.QualificationDocumentalPolicyToggling,
    doctorsManaging: AuthorizationPermissions.QualificationDocumentalDoctorsManaging
  }
}

/**
 * Gateia as acoes de escrita das paginas de Politicas de qualificacao.
 * O grant do Analista pode estar no nivel empresa (post_station_company) ou
 * unidade (post_station_health_unit) — por isso cada checagem faz OR entre os
 * dois resource types. Content Administrator+ herda ambos via cascata do backend.
 * Observacao: com a feature flag de permissoes desligada, checkAnyRessources
 * retorna true e todas as acoes permanecem visiveis (comportamento atual).
 */
export function useQualificationPolicyPermissions(domain: QualificationDomain) {
  const permissionsStore = usePermissionsStore()
  const perms = DOMAIN_PERMISSIONS[domain]

  const has = (permission: AuthorizationPermissions) =>
    permissionsStore.checkAnyRessources({
      permission,
      resourceType: AuthorizationResourcesAndSubjects.PostStationCompany
    }) ||
    permissionsStore.checkAnyRessources({
      permission,
      resourceType: AuthorizationResourcesAndSubjects.PostStationHealthUnit
    })

  return {
    canCreate: computed(() => has(perms.creating)),
    canEdit: computed(() => has(perms.editing)),
    canToggle: computed(() => has(perms.toggling)),
    canManageDoctors: computed(() => has(perms.doctorsManaging))
  }
}
